var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/extensions/ContentExtensionBoilerplate.ts
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { ComponentSizeController } from "@adornis/chemistry/controllers/component-size-controller.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-icon.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import "@adornis/dragdrop/x-drag-drop.js";
import { html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { BaseCustomContent } from "../../db/extensions/BaseCustomContent.js";
import {
  AnchorIDConsumer,
  AutoScrollContainerConsumer,
  ContentConsumer,
  DraggingConsumer,
  ExtensionsConsumer,
  IdentifierConsumer,
  ModeConsumer,
  SizeConsumer
} from "../globals/consumer.js";
import { Droppable } from "../globals/enums.js";
import { ThrowEventDraggingChanged, ThrowEventMoveToAnchor, ThrowEventSetEditContent } from "../globals/events.js";
import { convertDataBySize, getExtensionForData } from "../globals/methods.js";
import "./../x-buildify-dropzone.js";
var _ExtensionBoilerplate = class _ExtensionBoilerplate extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.content = new RXController(this, void 0);
    this.defaultValues = new RXController(this, void 0);
    this.isSubContent = false;
    this.hasDropzoneBefore = false;
    this.hasDropzoneAfter = false;
    this._consumedExtensions = ExtensionsConsumer(this);
    this._consumedAnchorID = AnchorIDConsumer(this);
    this._consumedDragging = DraggingConsumer(this);
    this._consumedIdentifier = IdentifierConsumer(this, false);
    this._consumedContent = ContentConsumer(this);
    this._consumedSize = SizeConsumer(this);
    this._componentSize = new ComponentSizeController(this, { triggerUpdates: false });
    this._consumedMode = ModeConsumer(this);
    this._consumedAutoScrollContainer = AutoScrollContainerConsumer(this);
  }
  willUpdate(_changedProperties) {
    super.willUpdate(_changedProperties);
    const content = this.content.value;
    const size = this._consumedSize.value;
    if (!content)
      return;
    const extension = getExtensionForData(content, this._consumedExtensions.value);
    const convertedContent = convertDataBySize(content, extension, size);
    this._convertedContent = convertedContent;
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (this.content.value?.htmlID) {
      this.id = this.content.value.htmlID;
    } else {
      this.removeAttribute("id");
    }
    if (this._consumedAnchorID.value && this._consumedAnchorID.value === this.content.value?.htmlID) {
      this.scrollIntoView({ behavior: "smooth" });
      ThrowEventMoveToAnchor(this, null);
    }
    if (_ExtensionBoilerplate.prototype.render !== Object.getPrototypeOf(this).render) {
      const message = "the render method is final! Override __content() method";
      this.renderRoot.innerHTML = `<span style="color: red;">${message}</span>`;
      throw new Error(message);
    }
  }
  shouldUpdate(_changedProperties) {
    return super.shouldUpdate(_changedProperties);
  }
  render() {
    if (this.content.isLoading)
      return html` <x-loading-state></x-loading-state> `;
    if (!this.content.value || !this._convertedContent && !(this.content.value instanceof BaseCustomContent))
      return "404: content not found";
    const content = xComponents(html`
      ${this.isEditMode && this._convertedContent?.display === "none" ? html`
            <x-flex
              center
              horizontal
              crossaxis-center
              space="sm"
              ${css({
      background: "#333",
      color: "#fff",
      position: "absolute",
      inset: "0",
      opacity: "0.75",
      zIndex: "9999999",
      pointerEvents: "none",
      userSelect: "none"
    })}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" width="24" height="24" style="fill: #fff">
                <path
                  d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c8.4-19.3 10.6-41.4 4.8-63.3c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zM373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5L373 389.9z"
                />
              </svg>
              <d-text> Element ausgeblendet </d-text>
            </x-flex>
          ` : nothing}
      ${this._content(this._convertedContent)}
    `);
    return html` ${content} ${this._states()} ${this._dropzones()} `;
  }
  getValue(key) {
    const currentWidth = this._consumedSize.value;
    const sizeKey = currentWidth ?? "global";
    const defaultValueProperty = this.defaultValues.value?.[key]?.[sizeKey] ?? this.defaultValues.value?.[key]?.["global"];
    const value = this._convertedContent?.[key];
    if (value || typeof value === "boolean")
      return value;
    if (defaultValueProperty || typeof defaultValueProperty === "boolean")
      return defaultValueProperty;
    return "";
  }
  _dropzones() {
    if (this.isEditMode)
      return html`
        ${this.hasDropzoneBefore ? html`
              <x-buildify-dropzone
                ${css({
        position: "absolute",
        top: "-5px",
        height: "10px",
        width: "100%",
        left: "0",
        overflow: "hidden"
      })}
                drag-over
                .index=${this.content.value?.index ?? 0}
                .subItemID=${this.content.value?.subItemID}
                .parentID=${this.content.value?.parentID}
              ></x-buildify-dropzone>
            ` : nothing}
        ${this.hasDropzoneAfter ? html`
              <x-buildify-dropzone
                ${css({
        position: "absolute",
        bottom: "-5px",
        height: "10px",
        width: "100%",
        left: "0",
        overflow: "hidden"
      })}
                drag-over
                .index=${(this.content.value?.index ?? 0) + 1}
                .subItemID=${this.content.value?.subItemID}
                .parentID=${this.content.value?.parentID}
              ></x-buildify-dropzone>
            ` : nothing}
      `;
    return nothing;
  }
  __wrapper(content) {
    return html`
      <div ${css({ width: "fit-content", height: "fit-content", display: "inline-block" })}>${content}</div>
    `;
  }
  _content(content) {
    return nothing;
  }
  _states() {
    if (this.isViewMode)
      return nothing;
    if (this._editContentInjected === this.content.value)
      return this.__inEdit();
    return this.__editHover();
  }
  __editHover() {
    if (this.isSubContent)
      return nothing;
    return html`
      <x-flex edit-hover @click=${(e) => this._editContent(e)}> </x-flex>
      ${this._dragHandle()}
    `;
  }
  _dragHandle() {
    return html`
      <x-drag-drop
        ${css({ width: "" })}
        drag-drop
        allow-from="[drag]"
        child-selector=".bar-item"
        .autoScrollContainer=${() => this._consumedAutoScrollContainer.value}
        @dragging-changed=${(e) => ThrowEventDraggingChanged(this, e.target.dragging)}
      >
        <x-flex
          class="bar-item"
          w100
          identifier=${ifDefined(this._consumedIdentifier.value)}
          .value=${this.content.value}
          .type=${Droppable.EXISTING_CONTENT}
        >
          <x-flex drag center crossaxis-center w100>
            ${this._consumedDragging.value ? nothing : html` <x-icon ${css({ fontSize: "12px" })}> drag_handle </x-icon> `}
          </x-flex>
        </x-flex>
      </x-drag-drop>
    `;
  }
  __inEdit() {
    return html` ${this.__inEditOverlay()} `;
  }
  __inEditOverlay() {
    return html`
      <x-flex
        in-edit
        center
        crossaxis-center
        @click=${(e) => {
      this._editContent(e);
    }}
      >
        <x-icon ${css({ fontSize: `min(${(this._componentSize.height ?? 1) / 2}px, 50px)` })}> edit </x-icon>
      </x-flex>
    `;
  }
  get isEditMode() {
    return this._consumedMode.value === "edit";
  }
  get isViewMode() {
    return !this.isEditMode;
  }
  _editContent(e) {
    e.preventDefault();
    e.stopPropagation();
    ThrowEventSetEditContent(this, this.content.value);
  }
  /**
   * this is used to programmatically open or activate any contents (e.g. open an accordeon for search highlights)
   * TODO hoping to deprecate this as soon as we support HTML as a storage format
   * @returns where to look for children in
   */
  async activate(desiredChildID) {
    return this.shadowRoot;
  }
  styles() {
    return [
      ...super.styles(),
      {
        ":host": {
          position: "relative",
          zIndex: "1",
          overflow: "visible !important",
          display: !this.isEditMode && this.getValue("display") ? `${this.getValue("display")} !important` : "",
          margin: this.getValue("margin"),
          padding: this.getValue("padding")
        },
        ":host>*": {
          // wenn es keine preview ist, soll der overflow ausgeblendet werden
          // -> sodass es keine Probleme mit dem Hover-Overlay gibt
          overflow: this.isViewMode ? "" : "hidden"
        },
        "[drag-drop]": {
          overflow: "visible",
          display: "none",
          position: "absolute",
          left: "0",
          top: "0",
          width: "14px",
          height: "100%"
        },
        "[edit-hover], [in-edit]": {
          position: "absolute",
          left: "0",
          top: "0",
          width: "100%",
          height: "100%",
          background: "rgba(232,232,232,.8)",
          zIndex: "10",
          cursor: "pointer",
          display: "none"
        },
        "[in-edit]": {
          display: "flex"
        },
        "[drag]": {
          height: "100%",
          left: "0px",
          background: "#333",
          zIndex: "10"
        },
        "[in-edit]>x-icon": {
          fontSize: "20px",
          color: "#333"
        },
        "[drag]>x-icon": {
          color: "#fff"
        },
        ":host(:hover) [edit-hover], :host(:hover) [drag-drop]": {
          display: "flex"
        }
      }
    ];
  }
};
__decorateClass([
  property({ attribute: false })
], _ExtensionBoilerplate.prototype, "content", 2);
__decorateClass([
  property({ attribute: false })
], _ExtensionBoilerplate.prototype, "defaultValues", 2);
__decorateClass([
  property({ attribute: "sub", type: Boolean })
], _ExtensionBoilerplate.prototype, "isSubContent", 2);
__decorateClass([
  property({ attribute: false, type: Boolean })
], _ExtensionBoilerplate.prototype, "hasDropzoneBefore", 2);
__decorateClass([
  property({ attribute: false, type: Boolean })
], _ExtensionBoilerplate.prototype, "hasDropzoneAfter", 2);
__decorateClass([
  state()
], _ExtensionBoilerplate.prototype, "_editContentInjected", 2);
var ExtensionBoilerplate = _ExtensionBoilerplate;
export {
  ExtensionBoilerplate
};
